import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../ui/button';
import Spinner from '../ui/spinner';
import type { useModal } from './new/use-modal';
import { Modal } from './new/modal';

export interface ConfirmationModalProps {
  title: string;
  description?: React.ReactNode;
  confirmationText?: string;
  onClose?: () => void;
  onSubmit: () => Promise<void> | void;
  modal: ReturnType<typeof useModal>;
  // highlights the confirm button in red for actions that remove or revoke something
  destructive?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  description,
  confirmationText,
  onClose,
  onSubmit,
  modal,
  destructive = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const closeModalHandler = () => {
    modal.dismiss();
    return onClose?.();
  };
  const submitHandler = async () => {
    try {
      setLoading(true);
      closeModalHandler();
      return await onSubmit();
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal {...modal} title={title} className="max-w-md">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-lighter/40">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      )}
      <Modal.Body className="pb-0 pt-2 sm:pb-0 sm:pt-2">
        {typeof title === 'string' ? (
          <p className="text-base text-text-secondary">{description}</p>
        ) : (
          description
        )}
      </Modal.Body>
      {/* DOM order matches the visual order, so keyboard focus moves from cancel to confirm */}
      <Modal.Actions className="flex-col sm:flex-row sm:justify-end">
        <Button onClick={modal.dismiss} variant="outline" className="sm:min-w-32">
          {t('cancel')}
        </Button>
        <Button
          onClick={submitHandler}
          variant={destructive ? 'destructive' : 'contained'}
          className="sm:min-w-32"
        >
          {confirmationText}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
